import { Injectable, OnDestroy } from '@angular/core';
import { CanActivate, CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';

import { from, Observable, of, Subscription, TeardownLogic } from 'rxjs';
import { take } from 'rxjs/operators';
import { BaseRoleInfo } from 'services/auth/auth.service.class';

import { UserService } from 'services/user/user.service';
import { SystemRole } from './roles';

@Injectable()
export class AuthenticatedGuard implements CanActivate, OnDestroy {
    private _subrelease: Subscription = new Subscription();
    get subrelease(): TeardownLogic {
        return this._subrelease;
    }
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }

    constructor(private userSvc: UserService) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        // At a minimum, only allow access if we have a valid authtoken stored for the current user.
        let routeOk = !!this.userSvc?.User?.AuthToken;

        // If there is optional data present, particularly the 'Roles' setting, we must also check to ensure that the
        // current user has a role that is in the provided list of necessary roles to use the route.
        const key = 'Roles';

        if (routeOk && route.data != null && route.data[key] != null) {
            // Get the user's current roles.
            const _userRoles: BaseRoleInfo[] = this.userSvc?.User?.Roles || [];

            // Does the user have a role that is in the provided list?
            const neededRoles = (route.data[key] || []) as SystemRole[];

            if (neededRoles.includes(SystemRole.All) && !_userRoles?.length) {
                routeOk = false;
            } else if (
                !neededRoles.includes(SystemRole.All) &&
                !neededRoles.some((nr) => !!_userRoles.find((ur) => ur.RoleName === nr))
            ) {
                // Cannot find any of the needed roles within the list of user owned roles. That is a failure.
                routeOk = false;
            }
        }

        if (!routeOk) {
            console.error('You are not authorized to view the requested page.');
        }

        return routeOk;
    }

    ngOnDestroy(): void {
        this._subrelease.unsubscribe();
    }
}

export interface UnsavedChanges {
    CanDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
    ChangeTrackingActive: boolean;
    SetupTracking: () => void;
    TakeDownTracking: () => void;
}

@Injectable()
export class UnsavedChangesGuard implements CanDeactivate<UnsavedChanges> {
    canDeactivate(target: UnsavedChanges): Observable<boolean> | Promise<boolean> | boolean {
        return target.CanDeactivate ? target.CanDeactivate() : true;
    }
}

@Injectable()
export class UnsavedChangesService {
    private _nativeListener: EventListenerOrEventListenerObject | null;
    private _tracker: null | (() => Observable<boolean> | Promise<boolean> | boolean) = null;

    // tslint:disable-next-line: no-any
    private _nativeChecker = (checker: any, event: any) => {
        const isClean = checker != null ? checker(true) : false;

        if (!isClean) {
            const msg = 'There may be unsaved changes on this page. Do you wish to continue leaving the page?';

            // tslint:disable-next-line: deprecation
            event = event || window.event;

            if (event) {
                event.returnValue = msg;
            }

            return msg;
        } else {
            return;
        }
        // tslint:disable-next-line: semicolon
    };

    constructor() {
        this._nativeListener = null;
    }

    // #region Clear Tracking Method

    ClearTracking(): void {
        if (this._nativeListener) {
            window.removeEventListener('beforeunload', this._nativeListener);
            this._nativeListener = null;
        }

        this._tracker = null;
    }

    // #endregion

    // #region Check Tracker Method

    CheckTracker(): Observable<boolean> {
        const self = this;

        if (self._tracker != null) {
            const result = self._tracker();
            let obsResult: Observable<boolean>;

            if (result instanceof Promise) {
                obsResult = from(result);
            } else if (!(result instanceof Observable)) {
                obsResult = of<boolean>(result).pipe(take(1));
            } else {
                obsResult = result as Observable<boolean>;
            }

            return obsResult.pipe(take(1));
        } else {
            return of<boolean>(true).pipe(take(1));
        }
    }

    // #endregion

    // #region Has Tracker

    HasTracker(): boolean {
        return this._tracker != null;
    }

    // #endregion

    // #region Set Tracking Method

    SetTracking(tracker: () => Observable<boolean> | Promise<boolean> | boolean): void {
        const self = this;

        if (self._nativeListener) {
            self.ClearTracking();
        }

        self._nativeListener = self._nativeChecker.bind(window, tracker);

        window.addEventListener('beforeunload', self._nativeListener);

        this._tracker = tracker;
    }

    // #endregion
}
